/**
 * Connections.
 *
 * Built against taskdojo-app/src/screens/ConnectionsScreen.js: your status at
 * the top, two tabs -- who you are connected to, and requests in both
 * directions -- and adding someone by email.
 *
 * The data layer was already right and is left alone. Worth stating why,
 * because it is the part that could quietly break the apps:
 *
 *  - accepting a request goes through the `acceptFriendRequest` Cloud
 *    Function, never a direct write. Accepting has to add each person to the
 *    other's `connections` array, and a client cannot write another user's
 *    profile. Doing it client-side would half-connect people.
 *  - a friendRequest document carries both sides denormalised --
 *    fromUserId/Email/Name and toUserId/Email/Name -- so either party can list
 *    their requests without reading the other's profile. All six fields are
 *    written, because the phone reads them.
 *  - sending to an address with no account calls `sendInviteEmail` instead of
 *    creating a dangling request.
 *
 * Nudge is not ported. It is a push notification to someone else's phone, with
 * its own screen behind it; a browser cannot deliver one, and a button that
 * looks like it sent something when it did not is worse than no button.
 */
import React, { useMemo, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useConnections, Connection } from '../../contexts/ConnectionsContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import { updateUserProfile } from '../../services/firebaseService';

/** The phone's STATUS_COLORS, value for value. */
const STATUS_COLORS = [
  { label: 'Green', value: '#4C9A2A' },
  { label: 'Amber', value: '#E8B33A' },
  { label: 'Red', value: '#E34234' },
  { label: 'Purple', value: '#8E5572' },
];

const initialOf = (c: { avatarEmoji?: string; displayName?: string; email?: string }) =>
  c.avatarEmoji || c.displayName?.charAt(0)?.toUpperCase() || c.email?.charAt(0)?.toUpperCase() || 'U';

const Connections: React.FC = () => {
  const { state } = useApp();
  const { colors, isDark } = useTheme();
  const input = useInputClass();
  const {
    connections, pendingRequests, sentRequests, sentInvites, isLoading,
    sendFriendRequest, acceptFriendRequest, declineFriendRequest,
    cancelFriendRequest, removeConnection, markRequestsAsRead,
  } = useConnections();

  const profile = state.user as any;
  const [tab, setTab] = useState<'connections' | 'requests'>('connections');
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<Connection | null>(null);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusText, setStatusText] = useState(profile?.statusText || '');
  const [statusColor, setStatusColor] = useState(profile?.statusColor || STATUS_COLORS[0].value);
  const [savingStatus, setSavingStatus] = useState(false);

  const requestCount = pendingRequests.length + sentRequests.length + sentInvites.length;
  const unread = pendingRequests.some((r: any) => !r.read);

  const openRequests = () => {
    setTab('requests');
    // Clears the dot in the nav, as the phone does on opening the tab.
    if (unread) markRequestsAsRead().catch(() => {});
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const to = email.trim().toLowerCase();
    if (!to) { setError('Enter an email address.'); return; }
    setBusy('send'); setError(''); setNotice('');
    try {
      const res = await sendFriendRequest(to);
      setNotice(res.invited
        ? `No account for ${to} yet — we've emailed them an invite.`
        : `Request sent to ${res.recipientName || to}.`);
      setEmail('');
      setAdding(false);
    } catch (err: any) {
      setError(err?.message || "Couldn't send that request.");
    } finally {
      setBusy(null);
    }
  };

  const act = async (id: string, fn: () => Promise<void>, failure: string) => {
    setBusy(id); setError('');
    try { await fn(); } catch (err: any) { setError(err?.message || failure); }
    finally { setBusy(null); }
  };

  const saveStatus = async () => {
    if (!state.user?.uid) return;
    setSavingStatus(true);
    try {
      await updateUserProfile(state.user.uid, {
        statusText: statusText.trim(),
        statusColor: statusText.trim() ? statusColor : '',
        statusUpdatedAt: new Date().toISOString(),
      } as any);
      setStatusOpen(false);
    } catch (err: any) {
      setError(err?.message || "Couldn't save your status.");
    } finally {
      setSavingStatus(false);
    }
  };

  const tabBtn = (active: boolean) =>
    `flex-1 py-2.5 rounded-[10px] text-[13.5px] font-extrabold transition-colors
     ${active ? 'bg-dojo-red text-white' : `${colors.text}`}`;

  const avatar = (c: any, size = 44) => (
    <span
      className={`rounded-[14px] flex items-center justify-center flex-shrink-0 text-[18px] font-extrabold
        ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-100'}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {initialOf(c)}
    </span>
  );

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Connections"
          action={
            <ActionButton label="Add a connection" onClick={() => { setAdding(true); setError(''); setNotice(''); }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6M22 11h-6" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: connections.length, label: 'Connected' },
            { value: pendingRequests.length, label: 'Incoming', color: pendingRequests.length > 0 ? '#E34234' : undefined },
            { value: sentRequests.length + sentInvites.length, label: 'Sent', color: isDark ? '#FEC200' : '#C08A1E' },
          ]}
        />

        {/* Your own status, as on the phone. */}
        <div className="px-5 mb-4">
          <button
            type="button"
            onClick={() => { setStatusText(profile?.statusText || ''); setStatusOpen(true); }}
            className={`w-full rounded-[14px] ${raised} ${colors.cardBorder} ${colors.card}
              px-4 py-3 flex items-center gap-2.5 text-left ${pressable}`}
          >
            {profile?.statusText ? (
              <>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: profile.statusColor || '#4C9A2A' }} />
                <span className="flex-1 min-w-0 text-[14px] font-bold truncate">{profile.statusText}</span>
                <span className={`text-[13px] font-bold ${colors.textSecondary}`}>Edit</span>
              </>
            ) : (
              <>
                <span className="text-[15px]">🙂</span>
                <span className={`flex-1 text-[14px] font-semibold ${colors.textSecondary}`}>
                  Set your status…
                </span>
              </>
            )}
          </button>
        </div>

        {/* Tabs */}
        <div className={`mx-5 mb-4 p-1 rounded-[14px] flex gap-1 ${isDark ? 'bg-dojo-surface' : 'bg-stone-100'}`}>
          <button type="button" onClick={() => setTab('connections')} className={tabBtn(tab === 'connections')}>
            Connections ({connections.length})
          </button>
          <button type="button" onClick={openRequests} className={`${tabBtn(tab === 'requests')} relative`}>
            Requests ({requestCount})
            {unread && (
              <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-dojo-red" />
            )}
          </button>
        </div>

        {(error || notice) && (
          <div
            role={error ? 'alert' : 'status'}
            className={`mx-5 mb-4 rounded-[14px] border-2 px-4 py-3 text-[13px] font-semibold
              ${error
                ? 'border-dojo-red/50 bg-dojo-red/10 text-dojo-red'
                : 'border-dojo-green/50 bg-dojo-green/10 text-dojo-greenDark'}`}
          >
            {error || notice}
          </div>
        )}

        <div className="px-5">
          {isLoading ? (
            <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
          ) : tab === 'connections' ? (
            connections.length === 0 ? (
              <div className="flex flex-col items-center text-center py-20">
                <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                  flex items-center justify-center mb-[18px]`}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.9" className={colors.textMuted}>
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" />
                  </svg>
                </div>
                <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">No connections yet</h2>
                <p className={`text-[15px] font-semibold mb-5 ${colors.textSecondary}`}>
                  Add someone by email to share tasks and lists with them.
                </p>
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                    px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold`}
                >
                  Add a connection
                </button>
              </div>
            ) : (
              <ul className="space-y-2 list-none p-0 m-0">
                {connections.map((c) => (
                  <li key={c.uid}>
                    <div className={`rounded-[14px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                      p-3 flex items-center gap-3`}>
                      {avatar(c)}
                      <div className="flex-1 min-w-0">
                        <p className="text-[16px] font-bold truncate">{c.displayName || 'User'}</p>
                        <p className={`text-[14px] truncate ${colors.textSecondary}`}>{c.email}</p>
                        {c.statusText && (
                          <span
                            className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full
                              text-[11.5px] font-bold max-w-full"
                            style={{
                              color: c.statusColor || '#4C9A2A',
                              backgroundColor: `${c.statusColor || '#4C9A2A'}20`,
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: c.statusColor || '#4C9A2A' }} />
                            <span className="truncate">{c.statusText}</span>
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setConfirmRemove(c)}
                        aria-label={`Remove ${c.displayName || c.email}`}
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
                          ${colors.textMuted} hover:text-dojo-red transition-colors`}
                      >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 11h-6" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : requestCount === 0 ? (
            <div className="flex flex-col items-center text-center py-20">
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">Nothing pending</h2>
              <p className={`text-[15px] font-semibold ${colors.textSecondary}`}>
                Requests you send and receive show up here.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {pendingRequests.length > 0 && (
                <section>
                  <h2 className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
                    Waiting on you
                  </h2>
                  <ul className="space-y-2 list-none p-0 m-0">
                    {pendingRequests.map((r: any) => (
                      <li key={r.id}>
                        <div className={`rounded-[14px] border-2 border-b-[3px] ${colors.card}
                          ${r.read ? colors.cardBorder : 'border-dojo-red'} p-3`}>
                          <div className="flex items-center gap-3">
                            {avatar(r.fromUser || { displayName: r.fromUserName, email: r.fromUserEmail })}
                            <div className="flex-1 min-w-0">
                              <p className="text-[16px] font-bold truncate">
                                {r.fromUser?.displayName || r.fromUserName || 'User'}
                              </p>
                              <p className={`text-[14px] truncate ${colors.textSecondary}`}>
                                {r.fromUser?.email || r.fromUserEmail}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <PrimaryButton
                              onClick={() => act(r.id, () => acceptFriendRequest(r.id), "Couldn't accept that.")}
                              disabled={busy === r.id}
                              className="flex-1 !py-2 !pt-[10px] !pb-2 !text-[14px]"
                            >
                              {busy === r.id ? 'Working…' : 'Accept'}
                            </PrimaryButton>
                            <SecondaryButton
                              onClick={() => act(r.id, () => declineFriendRequest(r.id), "Couldn't decline that.")}
                              disabled={busy === r.id}
                              className="flex-1 !py-2 !pt-[10px] !pb-2 !text-[14px]"
                            >
                              Decline
                            </SecondaryButton>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {sentRequests.length > 0 && (
                <section>
                  <h2 className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
                    Sent
                  </h2>
                  <ul className="space-y-2 list-none p-0 m-0">
                    {sentRequests.map((r: any) => (
                      <li key={r.id}>
                        <div className={`rounded-[14px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                          p-3 flex items-center gap-3`}>
                          {avatar({ displayName: r.toUserName, email: r.toUserEmail })}
                          <div className="flex-1 min-w-0">
                            <p className="text-[16px] font-bold truncate">{r.toUserName || 'User'}</p>
                            <p className={`text-[14px] truncate ${colors.textSecondary}`}>{r.toUserEmail}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => act(r.id, () => cancelFriendRequest(r.id), "Couldn't cancel that.")}
                            disabled={busy === r.id}
                            className={`text-[13px] font-extrabold flex-shrink-0 ${colors.textSecondary}
                              hover:text-dojo-red transition-colors disabled:opacity-50`}
                          >
                            Cancel
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {sentInvites.length > 0 && (
                <section>
                  <h2 className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
                    Invited by email
                  </h2>
                  <ul className="space-y-2 list-none p-0 m-0">
                    {sentInvites.map((i: any) => (
                      <li key={i.id}>
                        <div className={`rounded-[14px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                          p-3 flex items-center gap-3`}>
                          {avatar({ email: i.toEmail })}
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-bold truncate">{i.toEmail}</p>
                            <p className={`text-[13px] ${colors.textSecondary}`}>
                              Invited — they'll appear here once they sign up.
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add by email */}
      {adding && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setAdding(false)}>
          <form
            onSubmit={send}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-md ${colors.card} border-2 ${colors.cardBorder}
              rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <h2 className="text-[24px] font-black tracking-[-0.6px] mb-1">Add a connection</h2>
            <p className={`text-[13.5px] font-semibold mb-5 ${colors.textSecondary}`}>
              We'll send a request. If they don't have an account yet, we'll email them an invite instead.
            </p>

            <Field label="Their email" className="mb-5">
              <input
                type="email"
                className={input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                autoCapitalize="none"
                autoFocus
              />
            </Field>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <SecondaryButton onClick={() => setAdding(false)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton type="submit" disabled={busy === 'send'} className="flex-1">
                {busy === 'send' ? 'Sending…' : 'Send request'}
              </PrimaryButton>
            </div>
          </form>
        </div>
      )}

      {/* Status */}
      {statusOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setStatusOpen(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-md ${colors.card} border-2 ${colors.cardBorder}
              rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <h2 className="text-[24px] font-black tracking-[-0.6px] mb-1">Your status</h2>
            <p className={`text-[13.5px] font-semibold mb-5 ${colors.textSecondary}`}>
              Your connections see this next to your name.
            </p>

            <Field label="Status" className="mb-4">
              <input
                className={input}
                value={statusText}
                onChange={(e) => setStatusText(e.target.value)}
                placeholder="Heads down until Friday"
                maxLength={60}
                autoFocus
              />
            </Field>

            <Field label="Colour" className="mb-5">
              <div className="flex gap-2">
                {STATUS_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setStatusColor(c.value)}
                    aria-label={c.label}
                    aria-pressed={statusColor === c.value}
                    className={`w-10 h-10 rounded-xl border-2 transition-transform
                      ${statusColor === c.value ? 'scale-110 border-current' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c.value }}
                  />
                ))}
              </div>
            </Field>

            <div className="flex gap-3">
              <SecondaryButton
                onClick={() => { setStatusText(''); }}
                disabled={savingStatus || !statusText}
                className="flex-1"
              >
                Clear
              </SecondaryButton>
              <PrimaryButton onClick={saveStatus} disabled={savingStatus} className="flex-1">
                {savingStatus ? 'Saving…' : 'Save'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Removing is mutual and silent on the other side, so it asks. */}
      {confirmRemove && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Remove this connection?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              You and {confirmRemove.displayName || confirmRemove.email} will no longer be connected,
              and neither of you can assign the other tasks or share lists. They aren't told.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmRemove(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  const c = confirmRemove;
                  setConfirmRemove(null);
                  act(c.uid, () => removeConnection(c.uid), "Couldn't remove that connection.");
                }}
                className="flex-1"
              >
                Remove
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Connections;
